import {
  Outlet,
  createRootRoute,
  createRoute,
  createRouter,
  type RouterHistory,
} from "@tanstack/react-router";
import { z } from "zod";

import { OpaqueIdSchema, RelativeAppPathSchema } from "../shared/schemas";
import { AppFramePage } from "./routes/AppFramePage";
import { DashboardPage } from "./routes/DashboardPage";
import { LoginPage } from "./routes/LoginPage";
import { NotFoundPage } from "./routes/NotFoundPage";
import { AccountSettingsPage, AuditTrailPage } from "./routes/ShellSectionPage";
import { WorkspacePage } from "./routes/WorkspacePage";

const rootRoute = createRootRoute({
  component: Outlet,
  notFoundComponent: NotFoundPage,
});

export const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  validateSearch: (search) => z.strictObject({ org: OpaqueIdSchema.optional() }).parse(search),
  component: LoginPage,
});

const OrganizationSearchSchema = z.strictObject({
  org: OpaqueIdSchema.optional(),
});

export const dashboardRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  validateSearch: (search) => OrganizationSearchSchema.parse(search),
  component: DashboardPage,
});

const WorkspaceSearchSchema = OrganizationSearchSchema.extend({
  tab: z.enum(["users", "settings", "appearance"]).catch("users"),
}).strict();

export const workspaceRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/workspace",
  validateSearch: (search) => WorkspaceSearchSchema.parse(search),
  component: WorkspacePage,
});

export const auditTrailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/audit-trail",
  validateSearch: (search) => OrganizationSearchSchema.parse(search),
  component: AuditTrailPage,
});

export const accountRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/account",
  validateSearch: (search) => OrganizationSearchSchema.parse(search),
  component: AccountSettingsPage,
});

const AppSearchSchema = z.strictObject({
  path: RelativeAppPathSchema.catch("/"),
});

export const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/apps/$appInstanceId",
  parseParams: (params) => ({
    appInstanceId: OpaqueIdSchema.parse(params.appInstanceId),
  }),
  validateSearch: (search) => AppSearchSchema.parse(search),
  component: AppFramePage,
});

export const routeTree = rootRoute.addChildren([
  loginRoute,
  dashboardRoute,
  workspaceRoute,
  auditTrailRoute,
  accountRoute,
  appRoute,
]);

export function createAppRouter(history?: RouterHistory) {
  return createRouter({
    routeTree,
    ...(history ? { history } : {}),
    defaultPreload: "intent",
    defaultPreloadStaleTime: 10_000,
    scrollRestoration: true,
  });
}

export const router = createAppRouter();

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
